import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import translate from '../../../commonComponents/translate';
import withCSRF from '../../../commonComponents/withCSRF';
import Anchor from '../../../commonComponents/Anchor';
import ShopPriceEstimation from '../shopItems/ShopPriceEstimation';
import CartStateProp from '../propTypes/cartStateProp';
import UserCurrencyProp from '../../../commonComponents/propTypes/userCurrencyProp';
import StripeCardProp from '../../../commonComponents/propTypes/stripeCardProp';
import {
  compose,
  FREQUENCIES_TYPES,
  PAYMENT_METHOD,
} from '../../../services/utilityFunctions';
import TenantProp, {
  PAYMENT_PROVIDER,
} from '../../../commonComponents/propTypes/tenantProp';
import orderPriceProp from '../propTypes/orderPriceProp';
import CheckoutTerms from '../components/CheckoutTerms';
import PaymentMethods from '../../../commonComponents/modal/components/PaymentMethods';
import Loader from '../../../services/loader';
import VatTypesProp from '../../../commonComponents/forms/Address/VatTypesProp';
import CountryProp from '../../../commonComponents/forms/Address/CountryProp';
import AddressProp from '../../../commonComponents/propTypes/addressProp';
import Notifications from '../../../services/notifications';

function ShopProductPayment({
  t,
  cartState,
  userCurrency,
  creditCards,
  selectCreditCard,
  createOrder,
  tenant,
  goBack,
  onChangeNotes,
  orderDetails,
  isPaidWithCoins,
  orderAlreadyCreated,
  vatTypes,
  countries,
  handleCompanyBilling,
  updateCreditCards,
  directSellingActive,
  tabActive,
  companyBilling,
  onHandlePaymentTabs,
  isSubscription,
}) {
  const { paymentProviderType } = tenant?.settings;
  const [isLoading, setIsLoading] = useState(false);
  const handleLoading = loading => {
    setIsLoading(loading);
  };
  const loader = useRef(new Loader('#shopCheckoutPayment'));
  useEffect(() => {
    if (loader.current) {
      if (isLoading) {
        loader.current.show();
      } else {
        loader.current.hide();
      }
    }
  }, [isLoading]);

  /** Handling the different kind of payments */

  // Terms of conditions
  const [checkedTerms, setCheckedTerms] = useState(false);
  const handleCheckbox = () => {
    setCheckedTerms(!checkedTerms);
  };

  // HANDLE THE ACCOUNT HOLDER NAME FOR BANCONTACT
  const [accountHolderName, setAccountHolderName] = useState(null);

  // HANDLE THE 3 TYPES OF PAYMENT
  const disablePurchaseButton = () => {
    // when payment provider is manual or none, subscriptions should not work at the end of the checkout
    if (
      isSubscription &&
      isSubscription !== 'only_once' &&
      paymentProviderType !== PAYMENT_PROVIDER.STRIPE &&
      paymentProviderType !== PAYMENT_PROVIDER.STRIPE_INVOICES
    ) {
      return true;
    }
    // If order total is 0 card is not needed
    if (orderDetails.total === 0) {
      return !checkedTerms || cartState.isLoading;
    }
    if (
      orderDetails.total > 0 &&
      paymentProviderType === PAYMENT_PROVIDER.NONE
    ) {
      return true;
    }
    if (tabActive === PAYMENT_METHOD.CREDIT_CARD) {
      return (
        !cartState.selectedCreditCard || !checkedTerms || cartState.isLoading
      );
    }
    if (
      tabActive === PAYMENT_METHOD.BANK_TRANSFER ||
      tabActive === PAYMENT_METHOD.CRYPTO_MANUAL ||
      tabActive === PAYMENT_METHOD.CHEQUE ||
      tabActive === PAYMENT_METHOD.PAYPAL
    ) {
      return !checkedTerms || cartState.isLoading;
    }
    if (tabActive === PAYMENT_METHOD.PAID_BY_COMPANY) {
      return !cartState.companyBilling || !checkedTerms || cartState.isLoading;
    }
    return (
      !checkedTerms ||
      !accountHolderName ||
      accountHolderName.length === 0 ||
      cartState.isLoading
    );
  };

  const handleCreateOrder = () => {
    // is subscription there is a value in subscription and no card
    const isSubscriptionNoCard =
      isSubscription &&
      tabActive !== PAYMENT_METHOD.PAYPAL && // ignore if is paypal
      !cartState.selectedCreditCard &&
      orderDetails?.subtotal >= 0;
    // is subscription there is a discount (coins or coupons) and there is a value to that
    const isSubscriptionAndDiscount =
      isSubscription &&
      orderDetails?.subtotal > 0 &&
      orderDetails?.coupon?.code;

    // if one of the cases happen, show error
    if (isSubscriptionNoCard || isSubscriptionAndDiscount) {
      Notifications.showNotificationError(
        t('error'),
        t('ERROR_NO_CARD_SUBSCRIPTION'),
      );
      return;
    }

    if (orderDetails.total > 0 || isSubscription) {
      createOrder(tabActive, accountHolderName);
    } else {
      createOrder(PAYMENT_METHOD.BANK_TRANSFER);
    }
  };

  const handlePaymentMethod = (type, payload) => {
    switch (type) {
      case 'updateHolderName':
        setAccountHolderName(payload.accountHolderName);
        break;
      case 'updatedCardSelected':
        selectCreditCard(payload.cardId);
        break;
      case 'updatedUserCards':
        updateCreditCards(payload.cards);
        selectCreditCard(payload.cardId);
        break;
      case 'removeCreditCard':
        updateCreditCards(payload.cards);
        break;
      case 'companyBilling':
        handleCompanyBilling(payload);
        break;
      case 'changeTab':
        onHandlePaymentTabs(payload.tab);
        break;
      default:
        console.log('*Default', type, payload);
    }
  };
  const handleRemoveCard = () => {
    selectCreditCard(null);
  };

  // We should always select a credit card for product subscriptions even if order.total === 0
  const showPaymentMethods = orderDetails.total > 0 || isSubscription;

  return (
    <div
      id="shopCheckoutPayment"
      className="m-portlet--rounded m-portlet--rounded-force m--margin-top-10 m--margin-bottom-10 shop-checkout-step3"
    >
      <div className="m-portlet__body">
        <div className="row no-padding">
          <div className="col-lg-8 col-md-8">
            <div className="payment-type" />
            <div className="payment-manage line-list">
              <div className="m-portlet__body">
                {showPaymentMethods ? (
                  <PaymentMethods
                    t={t}
                    cards={creditCards}
                    updatePaymentMethod={handlePaymentMethod}
                    tabActive={tabActive}
                    accountHolderName={accountHolderName}
                    selectedCreditCard={cartState.selectedCreditCard}
                    handleIsLoading={handleLoading}
                    tenant={tenant}
                    vatTypes={vatTypes}
                    countries={countries}
                    companyBilling={companyBilling}
                    isSubscription={isSubscription}
                    onRemoveCard={handleRemoveCard}
                  />
                ) : (
                  <>
                    <h3>{t('shop.checkout.payment.notRequired')}</h3>
                    <hr />
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="col-lg-4 col-md-4 continue-colum">
            <ShopPriceEstimation
              t={t}
              userCurrency={userCurrency}
              cartState={cartState}
              orderDetails={orderDetails}
              isPaidWithCoins={isPaidWithCoins}
              directSellingActive={directSellingActive}
            />
            <div className="notes-card">
              <h3>{t('shop.cart.notes')}</h3>
              <textarea
                className="form-control notes-input"
                placeholder={t('shop.cart.notes.placeholder')}
                onChange={e => {
                  onChangeNotes(e.target.value);
                }}
                rows={5}
                disabled={orderAlreadyCreated}
              />
            </div>
            <CheckoutTerms
              onChange={handleCheckbox}
              checked={checkedTerms}
              tenant={tenant}
            />
            <Anchor
              onClick={handleCreateOrder}
              className={`landing-certification-browse btn m-btn--pill btn-dynamic loader-button${
                disablePurchaseButton() ? ' disabled' : ''
              }${
                cartState.isLoading
                  ? ' m-loader m-loader--right m-loader--light'
                  : ''
              }`}
            >
              {t('shop.cart.complete_order')}
            </Anchor>
            <Anchor
              className="btn m-btn--pill btn-metal shop-btnBack"
              onClick={() => {
                goBack('payment');
              }}
            >
              {t('back')}
            </Anchor>
          </div>
        </div>
      </div>
    </div>
  );
}

ShopProductPayment.propTypes = {
  t: PropTypes.func.isRequired,
  isPaidWithCoins: PropTypes.bool.isRequired,
  cartState: PropTypes.shape(CartStateProp.propType).isRequired,
  goBack: PropTypes.func.isRequired,
  onChangeNotes: PropTypes.func.isRequired,
  userCurrency: PropTypes.shape(UserCurrencyProp.propType).isRequired,
  creditCards: PropTypes.arrayOf(PropTypes.shape(StripeCardProp.propType))
    .isRequired,
  selectCreditCard: PropTypes.func.isRequired,
  createOrder: PropTypes.func.isRequired,
  tenant: PropTypes.shape(TenantProp.propTypes),
  orderDetails: PropTypes.shape(orderPriceProp.propType),
  orderAlreadyCreated: PropTypes.string,
  vatTypes: PropTypes.arrayOf(PropTypes.shape(VatTypesProp.propType))
    .isRequired,
  handleCompanyBilling: PropTypes.func.isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  updateCreditCards: PropTypes.func.isRequired,
  onHandlePaymentTabs: PropTypes.func.isRequired,
  directSellingActive: PropTypes.bool.isRequired,
  tabActive: PropTypes.string.isRequired,
  companyBilling: PropTypes.shape({
    address: PropTypes.shape(AddressProp.propType),
    name: PropTypes.string,
    phone: PropTypes.string,
    taxCode: PropTypes.string,
    vatType: PropTypes.string,
    vatNumber: PropTypes.string,
  }),
  isSubscription: PropTypes.oneOf(FREQUENCIES_TYPES),
};

ShopProductPayment.defaultProps = {
  tenant: {},
  orderDetails: PropTypes.shape(orderPriceProp.defaultProp),
  orderAlreadyCreated: null,
  companyBilling: {},
  isSubscription: null,
};

export default compose(translate, withCSRF)(ShopProductPayment);
